import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiFetch } from '../services/auth'

interface Employee {
  EmployeeId?: string
  FirstName?: string
  LastName?: string
  FullName?: string
  Email?: string
  VacationDaysSavedYear1?: number
  VacationDaysRegisteredSavedYear1?: number
  VacationDaysSavedYear2?: number
  VacationDaysRegisteredSavedYear2?: number
  VacationDaysSavedYear3?: number
  VacationDaysRegisteredSavedYear3?: number
  VacationDaysSavedYear4?: number
  VacationDaysRegisteredSavedYear4?: number
  VacationDaysSavedYear5?: number
  VacationDaysRegisteredSavedYear5?: number
  VacationDaysSavedYear6Plus?: number
  VacationDaysRegisteredSavedYear6Plus?: number
  VacationDaysSaved?: number
  VacationDaysRegisteredSaved?: number
}

export function Staff() {
  const { user } = useAuth()
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchEmployees()
  }, [])

  const fetchEmployees = async () => {
    try {
      const response = await apiFetch('/staff')

      // console.log('Response status:', response.status)
      // console.log('Response headers:', Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = errorText

        try {
          const parsedError = JSON.parse(errorText) as { error?: string }
          errorMessage = parsedError.error || errorText
        } catch {
          // keep raw text
        }

        throw new Error(errorMessage || `Failed to fetch employees: ${response.status} ${response.statusText}`)
      }

      const responseText = await response.text()
      // console.log('Response text:', responseText.substring(0, 200))
      
      let data
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        // console.error('JSON parse error:', parseError)
        throw new Error('Server returned invalid JSON response')
      }
      
      setEmployees(data.employees || [])
    } catch (err) {
      // console.error('Fetch error:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch employees')
    } finally {
      setLoading(false)
    }
  }

  const getVacationDaysForYear = (employee: Employee, yearIndex: number): number => {
    // yearIndex: 0 = last year, 1 = 2 years ago, 2 = 3 years ago, etc.
    // Fortnox Year1 = last year, Year2 = 2 years ago, ..., Year6Plus = 6+ years ago
    let saved = 0
    let registered = 0
    
    switch (yearIndex) {
      case 0: 
        saved = employee.VacationDaysSavedYear1 || 0
        registered = employee.VacationDaysRegisteredSavedYear1 || 0
        break
      case 1: 
        saved = employee.VacationDaysSavedYear2 || 0
        registered = employee.VacationDaysRegisteredSavedYear2 || 0
        break
      case 2: 
        saved = employee.VacationDaysSavedYear3 || 0
        registered = employee.VacationDaysRegisteredSavedYear3 || 0
        break
      case 3: 
        saved = employee.VacationDaysSavedYear4 || 0
        registered = employee.VacationDaysRegisteredSavedYear4 || 0
        break
      case 4: 
        saved = employee.VacationDaysSavedYear5 || 0
        registered = employee.VacationDaysRegisteredSavedYear5 || 0
        break
      case 5: 
        saved = employee.VacationDaysSavedYear6Plus || 0
        registered = employee.VacationDaysRegisteredSavedYear6Plus || 0
        break
      default: 
        return 0
    }
    
    return Math.max(0, saved - registered)
  }

  const getTotalNetVacationDays = (employee: Employee): number => {
    const saved = employee.VacationDaysSaved || 0
    const registeredSaved = employee.VacationDaysRegisteredSaved || 0
    return Math.max(0, saved - registeredSaved)
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 1 - i)

  const getRoleBasedTitle = () => {
    switch (user?.role) {
      case 'admin':
        return 'Sparade semesterdagar - Alla anställda'
      case 'lla':
        return 'Sparade semesterdagar - Dina medarbetare'
      case 'user':
        return 'Dina sparade semesterdagar'
      default:
        return 'Sparade semesterdagar'
    }
  }

  const getRoleBasedEmptyMessage = () => {
    switch (user?.role) {
      case 'admin':
        return 'Inga anställda registrerade än.'
      case 'lla':
        return 'Du har inga medarbetare registrerade eller så finns de inte i Fortnox med samma e-postadress.'
      case 'user':
        return 'Du finns inte i Fortnox med samma e-postadress som ditt Microsoft-konto.'
      default:
        return 'Du har inte behörighet att se semesterdata.'
    }
  }

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0'
      }}>
        <p>Laddar personal...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ 
          color: '#1a202c', 
          marginBottom: '16px',
          fontSize: '24px',
          fontWeight: '600'
        }}>
          Personal
        </h2>
        <div style={{
          padding: '24px',
          backgroundColor: '#fef2f2',
          borderRadius: '8px',
          border: '1px solid #fecaca'
        }}>
          <p style={{ color: '#dc2626', margin: '0 0 12px 0', fontWeight: '500' }}>
            Fel: {error}
          </p>
          <p style={{ color: '#64748b', margin: '0', fontSize: '14px' }}>
            Kontrollera att Fortnox är ansluten och att du har behörighet att läsa personaldata.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '32px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <h2 style={{ 
        color: '#1a202c', 
        marginBottom: '24px',
        fontSize: '24px',
        fontWeight: '600'
      }}>
        {getRoleBasedTitle()}
      </h2>
      
      {employees.length === 0 ? (
        <div style={{
          padding: '24px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <p style={{ color: '#64748b', margin: '0' }}>
            {getRoleBasedEmptyMessage()}
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e2e8f0',
                  color: '#374151',
                  fontWeight: '600'
                }}>
                  Namn
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  borderBottom: '2px solid #e2e8f0',
                  color: '#374151',
                  fontWeight: '600',
                  minWidth: '100px'
                }}>
                  Totalt sparade
                </th>
                {years.map((year, index) => (
                  <th key={year} style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    borderBottom: '2px solid #e2e8f0',
                    color: '#374151',
                    fontWeight: '600',
                    minWidth: '80px'
                  }}>
                    {index === years.length - 1 ? `${year} och äldre` : year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((employee, index) => (
                 <tr key={employee.EmployeeId} style={{                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                }}>
                   <td style={{
                     padding: '12px 16px',
                     borderBottom: '1px solid #e2e8f0',
                     color: '#1a202c',
                     fontWeight: '500'
                   }}>
                     {employee.FullName}
                   </td>
                   <td style={{
                     padding: '12px 16px',
                     borderBottom: '1px solid #e2e8f0',
                     textAlign: 'center',
                     color: '#1a202c',
                     fontWeight: '600'
                   }}>
                     <span style={{
                       display: 'inline-block',
                       padding: '6px 12px',
                       backgroundColor: '#f0f9ff',
                       color: '#0369a1',
                       borderRadius: '6px',
                       fontSize: '14px',
                       fontWeight: '700'
                      }}>
                        {getTotalNetVacationDays(employee)} dagar
                      </span>                   </td>
                   {years.map((year, yearIndex) => (
                     <td key={year} style={{
                       padding: '12px 16px',
                       borderBottom: '1px solid #e2e8f0',
                       textAlign: 'center',
                       color: '#1a202c',
                       fontWeight: '500'
                     }}>
                       <span style={{
                         display: 'inline-block',
                         padding: '4px 8px',
                         backgroundColor: '#dbeafe',
                         color: '#1e40af',
                         borderRadius: '4px',
                         fontSize: '14px'
                       }}>
                         {getVacationDaysForYear(employee, yearIndex)} dagar
                       </span>
                     </td>
                   ))}
                 </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
